import { ReferenceContactId, TaskTypeId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { logLabelForCategory } from "components/marketing-plan/marketing-plan-agenda";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { trpc } from "components/rpc/trpc";
import { ActivityTypeOption } from "components/tasks/activity-type-options";
import {
    LogActivityForm,
    LogActivitySubmission,
} from "components/tasks/log-activity-form";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import {
    attendeeCandidateOptions,
    attendeeCandidatesQueryOptions,
} from "components/visits/attendee-candidates";
import {
    ActivityParent,
    activityRecordTypeForActivityParentKind,
    VisitParent,
    visitParentFor,
} from "components/visits/types";
import { VisitEditor } from "components/visits/visit-editor";
import React from "react";
import { handleEvent } from "util/user-events";
import { activityCategoriesForParent } from "./activity-parent";
import { ActivityTypePickList } from "./activity-type-pick-list";

type WizardStep =
    | { kind: "pick" }
    | { kind: "log"; taskType: ActivityTypeOption | null }
    | { kind: "visit"; activityTypeId: TaskTypeId; visitParent: VisitParent };

interface LogActivityWizardProps {
    parent: ActivityParent;
    onClose: () => void;
}

export const LogActivityWizard: React.FC<LogActivityWizardProps> = props => {
    const { parent, onClose } = props;
    const visitParent = visitParentFor(parent);
    const categories = activityCategoriesForParent(parent);
    const [step, setStep] = React.useState<WizardStep>({ kind: "pick" });
    const [involvedContactIds, setInvolvedContactIds] = React.useState<
        ReferenceContactId[]
    >([]);
    const refreshAgenda = useAgendaRefresh();

    const businessId = parent.kind === "organization" ? parent.id : null;
    const showsAttendeePicker = step.kind === "log" && !!step.taskType && !!businessId;
    const trpcClient = trpc.useTRPC();
    const attendeeCandidatesQuery = useQuery(
        attendeeCandidatesQueryOptions(trpcClient, businessId),
    );
    const attendeeOptions = React.useMemo(
        () => attendeeCandidateOptions(attendeeCandidatesQuery.data),
        [attendeeCandidatesQuery.data],
    );
    const logActivityMutation = useMutation({
        ...trpcClient.touchpoint.logActivity.mutationOptions(),
    });

    const pickType = (option: ActivityTypeOption | null) => {
        setInvolvedContactIds([]);
        if (option?.category === "visit" && visitParent) {
            setStep({ kind: "visit", activityTypeId: option.id, visitParent });
        } else {
            setStep({ kind: "log", taskType: option });
        }
    };

    const logActivity = async (
        taskType: ActivityTypeOption | null,
        submission: LogActivitySubmission,
    ) => {
        await logActivityMutation.mutateAsync({
            parentEntityId: parent.id,
            category: taskType?.category ?? "other",
            activityTypeId: taskType?.id ?? null,
            note: submission.note ?? null,
            occurredAt: submission.happenedAt.toISOString(),
            involvedContactIds: showsAttendeePicker ? involvedContactIds : [],
        });
        handleEvent("activity-logged", { source: "detail-overview" });
        refreshAgenda();
        onClose();
    };

    return (
        <>
            <ResponsivePanel
                isOpen={step.kind === "pick"}
                type={PanelType.medium}
                headerText="Log activity"
                onDismiss={onClose}>
                <ActivityTypePickList
                    categories={categories}
                    recordType={activityRecordTypeForActivityParentKind(parent.kind)}
                    onPickType={option => pickType(option)}
                    onPickUntyped={() => pickType(null)}
                />
            </ResponsivePanel>
            <ResponsivePanel
                isOpen={step.kind === "log"}
                type={PanelType.medium}
                headerText={
                    step.kind === "log"
                        ? logLabelForCategory(step.taskType?.category ?? null)
                        : "Log activity"
                }
                onDismiss={onClose}>
                {step.kind === "log" ? (
                    <LogActivityForm
                        typeLabel={step.taskType?.name ?? "Task"}
                        category={step.taskType?.category ?? "other"}
                        submitLabel={logLabelForCategory(step.taskType?.category ?? null)}
                        isSubmitting={logActivityMutation.isPending}
                        attendeePicker={
                            showsAttendeePicker
                                ? {
                                      options: attendeeOptions,
                                      value: involvedContactIds,
                                      onChange: setInvolvedContactIds,
                                  }
                                : null
                        }
                        onSubmit={submission => logActivity(step.taskType, submission)}
                        onCancel={onClose}
                    />
                ) : null}
            </ResponsivePanel>
            {step.kind === "visit" ? (
                <VisitEditor
                    mode="complete"
                    parent={step.visitParent}
                    activityTypeId={step.activityTypeId}
                    onSaved={() => {
                        refreshAgenda();
                        onClose();
                    }}
                    onClose={onClose}
                />
            ) : null}
        </>
    );
};
